import { ClosingReportType } from 'components/restaurantInformation/closings';
import {
    MenuType,
    TaxType,
    OrderItemStatus,
    OrderStatus,
    PaymentMethod,
    OrderServices,
    SpicinessLevels,
    Diets,
    DietaryLaws,
    PrinterServerStatus,
} from 'constants/enums';
import { DishData, AdditionalDishSize, AdditionalPrice } from 'store/menuDetailsStore/reducer';
import { OrderItem } from 'store/ordersStore/reducer';

export interface RestaurantFilterModel {
    name: string;
    addressLine: string;
    zip: string;
    city: string;
    showCoolBoxes: boolean;
}

export interface UserFilterModel {
    name: string;
    restaurant: string;
    email: string;
    phoneNumber: string;
    position: string;
    roles: string[];
    restaurantId?: string;
}

export interface MenuCreateModel {
    name: string;
    description: string;
    photo: string;
    type: MenuType;
    price?: number;
    position: number;
    discount: number;
}

export interface MenuDetailsData {
    menuId: string;
    sectionId: string;
    sectionName: string;
}

export interface RenameSectionModel {
    name: string;
}

export interface AddSectionModel {
    name: string;
    price?: number;
    menuId: string;
    photo?: string;
}

export interface SectionDailyMenuModel {
    menuId?: string;
    name: string;
    price: number;
    photo: string;
    priceLevels?: AdditionalPrice[];
    isCustomPrice?: boolean;
}

export interface DeleteSectionModel {
    menuId: string;
    sectionId: string;
}

export interface DeleteDishModel {
    dishId: string;
    sectionId: string;
    menuId: string;
}

export interface AddExistingDishModel {
    dishId: string;
    sectionId: string;
    dish: DishData;
}

export interface EditDishModel {
    dishId: string;
    sectionId: string;
    menuId: string;
}

export interface DishCreateModel {
    name: string;
    shortName: string;
    description: string;
    shortDescription: string;
    photo: string;
    price: number;
    addToGallery: boolean;
    spicinessLevel: SpicinessLevels;
    diet: Diets;
    dietaryLaw: DietaryLaws;
    sectionId: string;
    categoryId: string;
    allergens: string[];
    ingredients: IngredientModel[];
    primeCost: number;
    dineInTax: TaxType;
    homeDeliveryTax: TaxType;
    takeAwayTax: TaxType;
    barcode: string;
    stockQuantity: number;
    minimumStock: number;
    notFood: boolean;
    color: string;
    labels: string[];
    size: string;
    additionalSizes: AdditionalDishSize[];
    priceLevels?: AdditionalPrice[];
    isCustomPrice?: boolean;
    externalId?: number | null;
    supplierName?: string | null;
    catalogId?: string;
}

export interface RestaurantTypesManagementModel {
    create: string[];
    delete: string[];
    rename: { id: string; name: string }[];
}

export interface IdModel {
    id: string;
}

export interface OrderItemStatusChangeModel {
    itemId: string;
    itemUniqueId: string;
    dailyMenuUniqueId?: string;
    status: OrderItemStatus;
}

export interface PositionOrderItemStatusChangeModel {
    orderId: string;
    itemId: string;
    status: OrderItemStatus;
    previousStatus: OrderItemStatus;
    dailyMenuId: string;
}

export interface OrderUpdateModel {
    orderItems: OrderItem[];
    deliveryTime: number;
    status: OrderStatus;
    paymentMethod: PaymentMethod;
    qrCodeName: string;
    service: OrderServices;
    priceLevelId?: string;
}

export interface UsersToRestaurantUserModel {
    restaurantId: string;
    restaurantName: string;
    usersIds: string[];
    isContactPersons: boolean;
}

export interface EmptyOrderModel {
    service: string;
    deliveryTime: number;
    name: string;
    city: string;
    addressLine: string;
    email: string;
    phoneNumber: string;
    comment: string;
    qrCodeName: string;
}

export interface RestaurantPaymentDetailsModel {
    firmName: string;
    firstName: string;
    lastName: string;
    address: string;
    zip: string;
    city: string;
    accountNumber: string;
    iban: string;
    vat: string;
    tax: number;
    currency: string;
    thanksMessage: string;
}

export enum DishIngredientsState {
    Fixed,
    Addition,
}

export interface IngredientModel {
    id?: string;
    name: string;
    price: number;
    categoryId: string;
    amount: number;
    unitOfMeasure: string;
    allergens: string[];
    isAddition?: boolean;
    isRemoval?: boolean;
    isAdditionChecked?: boolean;
    isRemovalChecked?: boolean;
    isAdditionCheckedModified?: boolean;
    isRemovalCheckedModified?: boolean;
    state?: number;
    modified?: boolean;
}

export interface UpdateIngredientModel {
    name: string;
    price: number;
    categoryId: string;
    amount: number;
    unitOfMeasure: string;
    id: string;
    lang: string;
    allergens: string[];
}

export interface UpdateCategoryModel {
    id: string;
    lang: 'en' | 'de';
    name: string;
}

export interface SectionEditModel {
    name: string;
    price: number;
}

export interface TaxRequestModel {
    standardRate: number;
    reducedRate: number;
    specialRateForAccommodation: number;
    noVAT: number;
}

export interface TaxResponseModel extends TaxRequestModel {
    id: string;
}

export interface SectionColorModel {
    color: string;
    isDaily: boolean;
}

export interface CopyMenuModel {
    name: string;
    description: string;
    availableFrom?: number;
    availableTo?: number;
    menuType: MenuType;
    discount: number;
    sections: SectionWithDishes[];
}

export interface SectionWithDishes {
    id: string;
    dishes: string[];
}

export interface LabelModel {
    name: string;
    icon: string;
}

export interface IdName {
    id: string;
    name: string;
}

export interface RestaurantsGroupsListFilter {
    name?: string;
    address?: string;
    zip?: string;
    city?: string;
    page?: number;
    limit?: number;
    showCoolBoxes?: boolean;
}

export interface CreateRestaurantGroupModel {
    name: string;
    restaurants: string[];
    managers: string[];
    menusIds: string[];
    catalogName?: string;
}

export interface EditRestaurantGroupModel {
    name: string;
    restaurants: string[];
    managers: string[];
}

export interface GroupRestaurantInfoModel extends IdName {
    address: string;
    zip: string;
    city: string;
    isCoolBox: boolean;
}

export interface RestaurantsGroupModel extends IdName {
    isDisabled: boolean;
    restaurants: GroupRestaurantInfoModel[];
}

export interface RestaurantsGroupDetailsModel extends RestaurantsGroupModel {
    isDisabled: boolean;
    managers: IdName[];
    catalogs: IdName[];
}

export interface PagedList<T> {
    list: T[];
    count: number;
}

export enum RestaurantCatalogDataType {
    Menu,
    Section,
    Dish,
    Ingredient,
}

export enum RestaurantCatalogDataUpdateAction {
    DisableEnable,
    ChangeAmount,
    ChangeWorkArea,
}

export interface UpdateRestaurantDataModel {
    /** Id of related model {@link RestaurantCatalogDataType} */
    id: string;
    /** Value for the update */
    payload: string;
    dataType: RestaurantCatalogDataType;
    action: RestaurantCatalogDataUpdateAction;
}

export interface StornoOrderItemModel {
    id: string;
    menuId: string;
    reasonId: string;
}

export interface ClosingModel {
    closingType: ClosingReportType;
}

export interface PrinterServerApiKey {
    apiKey: string;
}

export interface PrinterServer {
    id: string;
    restaurantId: string;
    name: string;
    status: PrinterServerStatus;
    isOnline: boolean;
    osVersion: string;
    lastSeenUtc?: string;
    printers: Printer[];
}

export interface CreatedPrinterServer extends PrinterServer, PrinterServerApiKey {}

export interface Printer {
    // printerId: string;
    name: string;
    address: string;
    model: string;
    isMain: boolean;
    workAreas: IdName[];
    printerStatus: PrinterInfoStatus;
}

export enum ReceiptType {
    Receipt,
    WorkareaTicket,
    NextCourse,
    OrdersOverview,
    InvoiceReceipt,
}

export interface TestPrintRequest {
    printerIp: string;
    receiptType: ReceiptType;
}

export enum PrinterInfoStatus {
    Active,
    Inactive,
    OutOfPaper,
}

export interface DishStock
    extends IdName,
        Pick<DishData, 'price' | 'stockQuantity' | 'minimumStock' | 'externalId' | 'supplierName'> {
    sold: number;
    restaurantId: string;
    restaurantName: string;
    catalogId: string | null;
    catalogName: string | null;
    groupId: string | null;
}

export interface RestaurantsStock {
    id: string;
    name: string;
    products: DishStock[];
    groups: RestaurantGroup[];
    restaurants: IdName[];
}

export interface RestaurantGroup extends IdName {
    restaurants: IdName[];
}

export interface CatalogDishData extends DishData {
    restaurantId: string;
    restaurantName: string;
}

export interface CatalogRestaurantDishModel {
    price: number;
    stock: number;
    externalId?: number;
    supplierName?: string | null;
    applyToAll: boolean;
}
